import {
    actionMailItems,
    addItemToCustomFolder,
    createCustomFolder,
    createForwardingBatch,
    deleteCustomFolder,
    editCustomFolder, FilterableMailBoxUrlSlugs,
    MailBoxFolderNameMap,
    MailBoxSlugs, MailBoxUrlSlugs,
    MailFolderUpdateType,
    MailItemUpdateType, PrivateMailBoxUrlSlugs,
    removeItemFromCustomFolder,
    updateMailNote,
    updateMarkMailItemAsRead,
    useCustomMailFolders, useInfiniteMailBox,
    useInfiniteMailFolders, useMailItem,
    useUpdateCustomFolders,
    useUpdateMailBox,
} from "@twigsby/client-data";
import {CustomMailFolderType} from "@twigsby/client-data/lib/Types/FolderTypes";
import {CreateForwardingBatchType} from "@twigsby/client-data/lib/Types/ForwardingBatchTypes";
import {MailItemType} from "@twigsby/client-data/lib/Types/MailItemTypes";
import {useComboBoxSimple} from "@twigsby/frontend-framework";
import {GetArrayIndexByKeyValue} from "@twigsby/helpers";
import React, {PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {DropCustomFolder} from "../Components/Mail/Events/CustomFolderEvents";
import ConfirmActionModal from "../Components/Mail/Modals/ConfirmActionModal";
import CreateCustomFolderModal from "../Components/Mail/Modals/CreateCustomFolderModal";
import {DeleteCustomFolderModal} from "../Components/Mail/Modals/DeleteCustomFolderModal";
import FilterMailboxModal from "../Components/Mail/Modals/FilterMailboxModal";
import {ForwardItemsModal} from "../Components/Mail/Modals/ForwardItemsModal";
import {
    MailboxFilterUrlMapping,
    MailboxUrlMapping,
    MailboxUrlParamsType,
    MapFolderSlugToId
} from "../Helpers/MailUrlMapping";
import {DeleteFilter, UpdateAllFilters} from "../Helpers/UrlSlugsAndFilters";
import useMailItemActions from "../Hooks/useMailItemActions";

import {
    CustomMailFoldersType,
    InlineUpdateType, MailboxContextType,
    MailboxInfoType,
    MailboxProviderType,
} from "../Types/MailboxProviderTypes";

const MailBoxGridSize = [
    '',
    'grid-item-md',
    'grid-item-lg',
]

const MailboxContext = React.createContext<MailboxContextType>({} as MailboxContextType);

const MailboxProvider = (props: PropsWithChildren<MailboxProviderType>) => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const updateMailBox = useUpdateMailBox();
    const updateCustomFolder = useUpdateCustomFolders();

    const [urlParams, setUrlParams] = useState<MailboxUrlParamsType>(params);
    const [parentFolderId, setParentFolderId] = useState<number>(-1);

    const initialFolder = urlParams.folderSlug ? urlParams.folderSlug : urlParams.clientFolderIds ? MailBoxSlugs[-1] : urlParams.mailTagIds ? MailBoxSlugs[-3] : urlParams.unreadStatus ? MailBoxSlugs[-2] : 'inbox';

    const {
        status: folderStatus,
        data: folderData,
        error: folderError,
    } = useInfiniteMailFolders();

    const {
        status: customFolderStatus,
        data: customFolderData,
        error: customFolderError,
    } = useCustomMailFolders();

    const {
        status: mailItemStatus,
        data: mailItemData,
        error: mailItemError,
    } = useMailItem(params.mailId as string);

    const {
        allSelected,
        visibleSelected,
        visibleIndeterminate,
        selectedItems,
        ToggleAll,
        ToggleSelected,
        ToggleVisible,
        ResetSelection,
    } = useComboBoxSimple();

    const selectedItemsRef = useRef<Array<any>>(selectedItems);

    const [itemMoving, setItemMoving] = useState<boolean>(false);
    const [gridView, setGridView] = useState<boolean>(localStorage.getItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-view-type`) === 'grid');
    const [gridSize, setGridSize] = useState<any>(localStorage.getItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-grid-size`) ? JSON.parse(localStorage.getItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-grid-size`) as string) : { size: 0, className: MailBoxGridSize[0] });
    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

    const [ createCustomFolderOpen , setCreateCustomFolderOpen] = useState<boolean>(false);
    const [ deleteCustomFolderOpen , setDeleteCustomFolderOpen] = useState<boolean>(false);
    const [ activeCustomFolderData , setActiveCustomFolderData] = useState<CustomMailFoldersType | null>(null);
    const [ confirmActionData, setConfirmActionData ] = useState<any>({ open: false, actionId: -1, mailIds: []});
    const [ forwardItemsData, setForwardItemsData ] = useState<any>({ open: false, mailIds: [], mailItems: []});

    const [ tagCollectionName, setTagCollectionName ] = useState<string>('');

    const [ mailFolderModificationActive, setMailFolderModificationActive ] = useState<boolean>(false);

    const [ mailNote, setMailNote ] = useState<InlineUpdateType>({ data: null, success: false, updating: false, error: false });

    const [userFilters, setUserFilters ] = useState<FilterableMailBoxUrlSlugs>(MailboxFilterUrlMapping(urlParams, location));
    const [privateFilters, setPrivateFilters ] = useState<PrivateMailBoxUrlSlugs>(MailboxUrlMapping(urlParams, location));
    const [parsedFilters, setParsedFilters ] = useState<MailBoxUrlSlugs>({...privateFilters, ...userFilters});

    const [mailBoxInfo, setMailBoxInfo] = useState<MailboxInfoType>({
        folderId: MapFolderSlugToId(urlParams),
        folderName: MailBoxFolderNameMap[initialFolder],
        folderSlug: initialFolder,
        mailTagIds: urlParams.mailTagIds ? parseInt(urlParams.mailTagIds as string) : undefined,
        incomingRecipientNames: urlParams.incomingRecipientNameIds ? parseInt(urlParams.incomingRecipientNameIds as string) : undefined,
        customFolderId: urlParams.clientFolderIds ? parseInt(urlParams.clientFolderIds as string) : undefined,
    });

    const [mailItemRequiresTopUp, setMailItemRequiresTopUp] = useState<boolean>(false);
    const [mailItemOnHold, setMailItemOnHold] = useState<boolean>(false);

    const {
        status,
        data,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        dataUpdatedAt
    } = useInfiniteMailBox(parsedFilters);

    const { selectionActions, mailItemActionsError, mailItemActionsStatus, FilterActions} = useMailItemActions();

    const [preItemUpdate, setPreItemUpdate] = useState<MailItemType | undefined>(mailItemData);

    const UpdateMailboxInfo = () => {
        const customFolder = customFolderData ? customFolderData[GetArrayIndexByKeyValue(customFolderData, 'id', parseInt(urlParams.clientFolderIds as string))] : null;
        const folderParentFolderId = customFolder && customFolder.parentFolderId ? customFolder.parentFolderId :  -1;

        if(urlParams.clientFolderIds && !customFolder) {
            if(parentFolderId !== -1) {
                navigate(`/mail/custom-folder/${parentFolderId}`);
            } else {
                navigate('/mail/inbox');
            }
        } else {
            const newData = {
                folderId: MapFolderSlugToId(urlParams),
                folderName: MailBoxFolderNameMap[urlParams.folderSlug as string] || customFolder && customFolder.description || urlParams.mailTagIds && `Tagged: ${tagCollectionName !== '' ? tagCollectionName : urlParams.mailTagIds}`,
                folderSlug: urlParams.clientFolderIds ? 'custom-folder' : urlParams.mailTagIds ? 'tagged' : urlParams.folderSlug || initialFolder,
                mailTagIds: urlParams.mailTagIds ? parseInt(urlParams.mailTagIds as string) : undefined,
                customFolderId: urlParams.clientFolderIds ? parseInt(urlParams.clientFolderIds as string) : undefined,
            };

            setMailBoxInfo((prevState) => {
                if(prevState.folderSlug !== (urlParams.folderSlug || initialFolder)) {
                    ResetSelection();
                }

                console.warn('UpdateMailboxInfo: FIX?', newData);
                return newData
            });
        }

        setParentFolderId(folderParentFolderId);
    }

    const MoveSelectedItems = useCallback((targetFolderId: number) => {
        if(selectedItems.length > 0) {
            MoveItemsToFolder(targetFolderId);
            ResetSelection();
        }
    }, [selectedItems]);

    const MoveItemsToFolder = (targetFolderId: number) => {
        setItemMoving(true);

        addItemToCustomFolder(urlParams.mailId ? [urlParams.mailId] : selectedItems, targetFolderId).then(() => {
            updateMailBox.mutate({
                newData: {
                    mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                    folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
                    targetFolderId: targetFolderId,
                },
                updateType: MailItemUpdateType.MOVE,
                mailBoxUrlSlugs: parsedFilters
            });
        }).finally(() => {
            ResetSelection();
            setItemMoving(false);
        })
    };

    const RemoveItemsFromFolder = (currentFolderId: number) => {
        setItemMoving(true);

        removeItemFromCustomFolder(urlParams.mailId ? [urlParams.mailId] : selectedItems, currentFolderId).then(() => {
            updateMailBox.mutate({
                newData: {
                    mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                    folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
                    targetFolderId: currentFolderId,
                },
                updateType: MailItemUpdateType.REMOVE,
                mailBoxUrlSlugs: parsedFilters
            });
        }).finally(() => {
            ResetSelection();
            setItemMoving(false);
        })
    };

    const UpdateMailTags = (tag: any, checked: boolean) => {
        updateMailBox.mutate({
            newData: {
                mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                tags: {...tag},
                isChecked: checked,
                folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug
            },
            updateType: MailItemUpdateType.TAGS,
            mailBoxUrlSlugs: parsedFilters
        });
    }

    const UpdateMailNote = (noteValue: string, oldNoteValue: string) => {
        if(noteValue !== oldNoteValue) {
            setMailNote({ data: null, success: false, updating: true, error: false });
            updateMailNote(urlParams.mailId as string, noteValue)
                .then((r) => {
                    updateMailBox.mutate({
                        newData: {
                            mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                            noteContent: noteValue
                        },
                        updateType: MailItemUpdateType.NOTE,
                        mailBoxUrlSlugs: parsedFilters
                    });
                    setMailNote({ data: r, success: true, updating: false, error: false });
                })
                .catch((ex) => {
                    setMailNote({ data: ex, success: false, updating: false, error: true });
                })
        }
    };

    const MarkMailItemAsRead = () => {
        updateMarkMailItemAsRead(urlParams.mailId as string).then(() => {
            updateMailBox.mutate({
                newData: {
                    mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                    dateRead: new Date()
                },
                updateType: MailItemUpdateType.MARK_AS_READ,
                mailBoxUrlSlugs: parsedFilters
            });
        })
    };

    const ActionMailItemsUi = (actionName: string) => {
        updateMailBox.mutate({
            newData: {
                mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
                newStatus: {
                    key: 'test_key',
                    description: actionName,
                    status: 'pending',
                    requiresUserInput: false
                }
            },
            updateType: MailItemUpdateType.MAIL_ACTION,
            mailBoxUrlSlugs: parsedFilters
        });

        updateMailBox.mutate({
            newData: {
                mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
            },
            updateType: MailItemUpdateType.REMOVE,
            mailBoxUrlSlugs: parsedFilters
        });
    }

    const UndoActionMailItems = () => {
        updateMailBox.mutate({
            newData: {
                mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
                newStatus: preItemUpdate?.mailAction,
                newActions: preItemUpdate?.actions
            },
            updateType: MailItemUpdateType.UNDO_MAIL_ACTION,
            mailBoxUrlSlugs: parsedFilters
        });

        updateMailBox.mutate({
            newData: {
                mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
                folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
            },
            updateType: MailItemUpdateType.RESTORE,
            mailBoxUrlSlugs: parsedFilters
        });
    }

    const ActionMailItems = (actionId: number, actionName: string, successCallback?: any) => {
        ActionMailItemsUi(actionName);
        ResetSelection();
        actionMailItems(urlParams.mailId ? [urlParams.mailId] : selectedItems, actionId).then(() => {
            //ActionMailItemsUi();
        }).finally(() => {
            if(successCallback) {
                successCallback();
            }
        })
    }

    const SetGridView = (value: boolean) => {
        localStorage.setItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-view-type`, value ? 'grid' : 'list');
        setGridView(value);
    }

    const SetGridSize = (size: number) => {
        const newValue = { size: size, className: MailBoxGridSize[size] };
        localStorage.setItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-grid-size`, JSON.stringify(newValue));
        setGridSize(newValue);
    }

    const ToggleCreateCustomFolderModal = (state: boolean) => {
        setCreateCustomFolderOpen(state);
        if(!state && activeCustomFolderData) {
            setActiveCustomFolderData(null);
        }
    }

    const ToggleDeleteCustomFolderModal = (state: boolean) => {
        setDeleteCustomFolderOpen(state);
        if(!state && activeCustomFolderData) {
            setActiveCustomFolderData(null);
        }
    }

    const UpdateActiveFolderData = (folderId: number) => {
        if(!customFolderData) return;
        const folderIndex = GetArrayIndexByKeyValue(customFolderData, 'id', folderId);
        const parentFolderIndex = GetArrayIndexByKeyValue(customFolderData, 'parentFolderId', folderId);

        setActiveCustomFolderData({...customFolderData[folderIndex], childFolders: parentFolderIndex > -1});
    }

    const CreateCustomFolder = (newFolderData: CustomMailFolderType, SuccessCallback?: (newFolderId: number) => void) => {
        let newFolder : CustomMailFolderType = {
            id: Math.floor(Math.random() * (1000 - 1 + 1) + 1),
            description: newFolderData.description,
            colour: newFolderData.colour,
            parentFolderId: newFolderData.parentFolderId !== -1 ? newFolderData.parentFolderId : null
        };

        createCustomFolder(newFolderData).then((resp) => {
            updateCustomFolder.mutate({
                newData: {
                    folderData: {
                        ...newFolder,
                        id: resp.id
                    }
                },
                updateType: MailFolderUpdateType.ADD
            })

            ToggleCreateCustomFolderModal(false);

            if(selectedItems.length > 0) {
                MoveItemsToFolder(resp.id)
            }

            if(SuccessCallback) {
                SuccessCallback(resp.id);
            }
        });
    }

    const EditCustomFolder = (editFolderData: CustomMailFolderType, SuccessCallback?: () => void) => {
        if(!activeCustomFolderData) return;
        setMailFolderModificationActive(true);

        let newFolderData : any = {};

        newFolderData.id = activeCustomFolderData.id;
        newFolderData.description = editFolderData.description;
        newFolderData.colour = editFolderData.colour;
        newFolderData.parentFolderId = editFolderData.parentFolderId !== -1 ? editFolderData.parentFolderId : null

        editCustomFolder(newFolderData).then((resp) => {
            updateCustomFolder.mutate({
                newData: {
                    targetFolderId: activeCustomFolderData.id,
                    folderData: newFolderData
                },
                updateType: MailFolderUpdateType.EDIT
            });

            ToggleCreateCustomFolderModal(false);

            if(SuccessCallback) {
                SuccessCallback();
            }
        }).finally(() => {
            setMailFolderModificationActive(false);
        })
    }

    const DeleteCustomFolder = (deleteChildFolders = false) => {
        if(!activeCustomFolderData) return;
        setMailFolderModificationActive(true);

        deleteCustomFolder(activeCustomFolderData?.id, deleteChildFolders).then(() => {
            updateCustomFolder.mutate({
                newData: {
                    targetFolderId: activeCustomFolderData.id,
                    deleteChildFolders: deleteChildFolders
                },
                updateType: MailFolderUpdateType.REMOVE
            });

            ToggleDeleteCustomFolderModal(false);
        }).finally(() => {
            setMailFolderModificationActive(false);
        })
    };

    const MoveCustomFolder = (targetFolderId: number | null, currentFolderId: number) => {
        if(!customFolderData) return;
        setMailFolderModificationActive(true);

        const folderIndex = GetArrayIndexByKeyValue(customFolderData, 'id', currentFolderId);
        let newData = {...customFolderData[folderIndex]};
        newData.parentFolderId = targetFolderId;

        editCustomFolder(newData).then(r => {
            updateCustomFolder.mutate({
                newData: {
                    targetFolderId: targetFolderId,
                    currentFolderId: currentFolderId
                },
                updateType: MailFolderUpdateType.CHANGE_PARENT
            });
        }).finally(() => {
            setMailFolderModificationActive(false);
        });
    }

    const GetMailItemsByUuid = (Uuids: Array<string>): Array<MailItemType> => {
        const collection: Array<MailItemType> = [];

        data?.pages.forEach((page) => {
            page.data.forEach((item) => Uuids.includes(item.mailUuid) ? collection.push(item) : null)
        })

        return collection;
    }

    const CreateForwardingBatch = (data: CreateForwardingBatchType) => {
        createForwardingBatch(data as CreateForwardingBatchType)
            .then((r) => {
                navigate(`/mail/forwarding-batches/${r.batchId}`);
                ForwardItemsComplete();
            })
            .catch((e) => console.log('create error', e))
    }

    const ActionItem = (actionId: number) => {
        if(actionId === 0) {
            const mailItems = GetMailItemsByUuid(urlParams.mailId ? [urlParams.mailId] : selectedItems);
            setForwardItemsData({ open: true, mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems, mailItems: mailItems });
        } else {
            setConfirmActionData({ open: true, actionId: actionId, mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems });
        }
    }

    const ForwardItemsComplete = () => {
        ActionMailItemsUi('Forward');
        setForwardItemsData({ open: false, mailIds: [], mailItems: [] });
        ResetSelection();
    }

    const ActionItemComplete = (actionId: number) => {
        setConfirmActionData({ open: false, actionId: -1, mailIds: [] });
        ResetSelection();
        if(actionId === 6 || actionId === 7) {
            navigate('/mail/inbox');
        }
    }

    const UpdateTagCollectionName = (value: string) => {
        setTagCollectionName(value)
    }

    const HandleFolderMove = (e: any) => {
        const newResult = e.currentId ? e : DropCustomFolder(e);

        if(newResult.targetId && newResult.currentId) {
            MoveCustomFolder(newResult.targetId, newResult.currentId);
        }
    }

    const ShowFilters = () => {
        setShowFilterModal(true);
    }

    const SetFilters = (filters: FilterableMailBoxUrlSlugs) => {
        const newFilters = UpdateAllFilters(filters);
        setUserFilters(filters);
        setParsedFilters({...privateFilters, ...newFilters});
    }

    const RemoveFilter = (filterName: keyof FilterableMailBoxUrlSlugs) => {
        const newFilters = DeleteFilter(userFilters, filterName);
        setUserFilters(newFilters);
        setParsedFilters({...privateFilters, ...newFilters});
    }

    const ClearFilters = () => {
        setUserFilters({});
        setParsedFilters({...privateFilters});
    }

    const UpdateAllFiltersAndParams = () => {
        const newFilterable = MailboxFilterUrlMapping(params, location);
        const newPrivate = MailboxUrlMapping(params, location);

        ResetSelection();
        setUrlParams(params);
        setUserFilters(newFilterable);
        setPrivateFilters(newPrivate);
        setParsedFilters({...newPrivate, ...newFilterable});
    }

    useEffect(() => {
        if(urlParams.mailId && mailItemData) {
            setPreItemUpdate(mailItemData);
            setMailItemOnHold(mailItemData?.holdReason !== null);
            setMailItemRequiresTopUp(mailItemData?.files && mailItemData?.files.length > 0 && mailItemData?.files[0].topupRequired);

            FilterActions([mailItemData], [mailItemData?.mailUuid]);
        }

        if(!urlParams.mailId && data) {
            FilterActions(data, selectedItems);
        }
    }, [allSelected, selectedItems, data, urlParams.mailId, mailItemData]);

    useEffect(() => {
        if((urlParams.mailTagIds || urlParams.mailId || urlParams.clientFolderIds || urlParams.folderSlug || urlParams.unreadStatus || urlParams.mailType) && customFolderStatus !== 'loading') {
            UpdateMailboxInfo();
            setMailNote({ data: null, success: false, updating: false, error: false });
        }
    }, [urlParams, customFolderData]);

    useEffect(() => {
        UpdateAllFiltersAndParams();
    }, [params, location]);

    useEffect(() => {
        selectedItemsRef.current = selectedItems;
    }, [selectedItems]);

    const mailboxFilterMemo = useMemo(() => {
        return {
            mailBoxInfo,

            status,
            data,
            error,
            isFetchingNextPage,
            fetchNextPage,
            hasNextPage,
            dataUpdatedAt,

            folderStatus,
            folderData,
            folderError,

            customFolderStatus,
            customFolderData,
            customFolderError,

            mailItemStatus,
            mailItemData,
            mailItemError,

            userFilters,
            privateFilters,
            parsedFilters,

            UpdateMailTags,
            MarkMailItemAsRead,
            UpdateMailNote,
            MoveSelectedItems,
            MoveItemsToFolder,
            RemoveItemsFromFolder,

            ActionMailItemsUi,
            UndoActionMailItems,

            ActionMailItems,

            ActionItem,
            ActionItemComplete,

            ShowFilters,
            SetFilters,
            RemoveFilter,
            ClearFilters,

            itemMoving,

            allSelected,
            visibleSelected,
            visibleIndeterminate,
            selectedItems,
            selectedItemsRef,

            ToggleAll,
            ToggleSelected,
            ToggleVisible,
            ResetSelection,

            gridSize,
            gridView,
            SetGridView,
            SetGridSize,

            FilterActions,
            selectionActions,
            mailItemActionsError,
            mailItemActionsStatus,

            createCustomFolderOpen,
            deleteCustomFolderOpen,
            activeCustomFolderData,

            ToggleCreateCustomFolderModal,
            ToggleDeleteCustomFolderModal,
            UpdateActiveFolderData,
            CreateCustomFolder,
            EditCustomFolder,
            DeleteCustomFolder,
            MoveCustomFolder,
            mailFolderModificationActive,

            HandleFolderMove,

            tagCollectionName,
            UpdateTagCollectionName,

            CreateForwardingBatch,

            mailNote,

            mailItemRequiresTopUp,
            mailItemOnHold
        }
    }, [
        mailFolderModificationActive,
        itemMoving,
        gridSize,
        gridView,
        allSelected,
        visibleSelected,
        visibleIndeterminate,
        selectedItems,
        selectedItemsRef,
        mailBoxInfo,
        status,
        data,
        error,
        isFetchingNextPage,
        hasNextPage,
        dataUpdatedAt,
        folderStatus,
        folderData,
        folderError,
        customFolderStatus,
        customFolderData,
        customFolderError,

        userFilters,
        privateFilters,
        parsedFilters,

        selectionActions,
        mailItemActionsError,
        mailItemActionsStatus,

        mailItemStatus,
        mailItemData,
        mailItemError,

        createCustomFolderOpen,
        deleteCustomFolderOpen,
        activeCustomFolderData,

        tagCollectionName,
        mailNote,

        mailItemRequiresTopUp,
        mailItemOnHold
    ]);

    return (
        <MailboxContext.Provider value={mailboxFilterMemo}>
            {props.children}

            <FilterMailboxModal
                isOpen={showFilterModal}
                closeCallback={() => setShowFilterModal(false)}
            />

            <CreateCustomFolderModal
                isOpen={createCustomFolderOpen}
                closeCallback={() => ToggleCreateCustomFolderModal(false)}
            />

            <DeleteCustomFolderModal
                isOpen={deleteCustomFolderOpen}
                closeCallback={() => ToggleDeleteCustomFolderModal(false)}
            />

            <ForwardItemsModal
                isOpen={forwardItemsData.open}
                mailIds={forwardItemsData.mailIds}
                mailItems={forwardItemsData.mailItems}
                closeCallback={() => setForwardItemsData({ open: false, mailIds: [], mailItems: [] })}
                actionedCallback={() => ForwardItemsComplete()}
            />

            <ConfirmActionModal
                isOpen={confirmActionData.open}
                folderSlug={mailBoxInfo.folderSlug}
                mailIds={confirmActionData.mailIds}
                actionId={confirmActionData.actionId}
                closeCallback={() => setConfirmActionData({ open: false, actionId: -1, mailIds: [] })}
                actionedCallback={() => ActionItemComplete(confirmActionData.actionId)}
            />

        </MailboxContext.Provider>
    )
}

const useMailbox = (pageId?: number, userId?: string) => useContext(MailboxContext);

export { MailboxContext, useMailbox };

export default MailboxProvider
